import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CancelOrderModal from "../../components/modals/CancelOrderModal";
import { UserContext } from "../../contexts/userContext";
import { IAlert } from "../../interfaces/alert";
import {
  IOrder,
  IGetOrdersParams,
  IOrdersFilters,
  IMappedOrder,
  TTabsProps,
  IOrdersComponent,
  IOrderRawWithMerchantName,
  IOrdersFiltersV2,
  IOrdersListV2Response,
  IServiceType,
} from "../../interfaces/order";
import { IPaginationParams } from "../../interfaces/table";
import {
  CARRIERS_TAB_ID,
  ERROR,
  GET,
  INFO,
  MEDIUM,
  MERCHANT,
  OWN_FLEET_TAB_ID,
  ROWS_PER_PAGE,
} from "../../utils/constants";
import { fetchData } from "../../utils/dataProvider";
import {
  downloadFromURL,
  getFallenServicesMessage,
  getFiltersQuery,
  getMappedInfo,
  buildOrdersV2Query,
  mapLegacyOrdersFiltersToV2,
} from "../../utils/orders";
import config from "../../config/config";
import { integrationIsPaused } from "../../utils/integrations";
import Loader from "../../components/Loader";
import EditOrderModal from "../../components/modals/EditOrderModal";
import { getStatusAliasList } from "../../requests/orderRequests";
import OrdersTabs from "../../components/orders/tabs/OrdersTabs";

const getOrders = async ({
  entity,
  setOrders,
  setAlert,
  setPagination,
  setLoading = () => undefined,
  selectedMarketplace,
  filters,
  newPage = 0,
  // v2 metadata setters (opcionales)
  setAvailableSorts,
  setAppliedSort,
  setAllowedStatusIds,
  setAvailableFilters,
}: IGetOrdersParams & {
  setAvailableSorts?: (_values: string[]) => void;
  setAppliedSort?: (_value?: string) => void;
  setAllowedStatusIds?: (_values?: string[]) => void;
  setAvailableFilters?: (_values?: IOrdersListV2Response["available_filters"]) => void;
}) => {
  const v2Enabled = !!config.ORDERS_V2_ENABLED;
  const skip = newPage * ROWS_PER_PAGE;
  try {
    setAlert({ showAlert: false } as IAlert);
    let url = "";
    if (v2Enabled) {
      // Map legacy filters to v2 deepObject structure
      const v2Filters: IOrdersFiltersV2 = mapLegacyOrdersFiltersToV2(filters, {
        page: newPage,
        limit: ROWS_PER_PAGE,
      });
      // Always send marketplaceId (mandatory in v2)
      url = `/v2/orders${buildOrdersV2Query(entity, selectedMarketplace, v2Filters)}`;
    } else {
      const useMarketplaceid =
        entity === MERCHANT ? `&marketplaceId=${selectedMarketplace}` : "";
      const hasFilters =
        Object.keys(filters).length > 0 ? getFiltersQuery(filters) : "";
      url = `/orders?limit=${ROWS_PER_PAGE}&skip=${skip}${useMarketplaceid}${hasFilters}`;
    }

    const response = await fetchData({ url, method: GET });

    if (v2Enabled) {
      const {
        results = [],
        count,
        available_filters,
        available_sorts,
        applied_sort,
        fallen_services,
      } = response as IOrdersListV2Response;

      if (fallen_services?.length)
        setAlert({
          typeOfAlert: INFO,
          message: getFallenServicesMessage(fallen_services),
          showAlert: true,
        });
      const legacyLike: IOrderRawWithMerchantName[] = results.map((r) => {
        const mappedService = r.service as unknown as IServiceType;
        return {
          _id: r._id,
          carrier: r.carrier_name || "",
          client_id: r.client_id,
          createdAt: r.createdAt,
          customer_document: r.customer_document || "",
          customer_document_type: r.customer_document_type || "",
          external_id: r.external_id,
          hash: r.hash || "",
          merchant_id: r.merchant_id,
          orderInfo: (r.external_order ? r.external_order : {}) as unknown as IOrder,
          retries: r.retries,
          service: mappedService,
          slaIsBroken: r.sla_is_broken ?? 0,
          sla_alert_level: r.sla_alert_level,
          status: r.status,
          status_history: r.status_history,
          tracking_id: r.tracking_id ?? "",
          updatedAt: r.updatedAt,
          merchant: r.merchant_name,
          merchant_name: r.merchant_name,
          alias: undefined,
          customization_fee: r.customization_fee,
          rejection_info: r.rejection_info || [],
          shipment_units: r.shipment_units || [],
        };
      });

      if (legacyLike.length) {
        setOrders(getMappedInfo(legacyLike));
        setPagination({ count, page: newPage });
      } else {
        setAlert({
          typeOfAlert: INFO,
          message: "No hay órdenes para mostrar.",
          showAlert: true,
        });
        setOrders([]);
      }
      // Exponer metadata de filtros/sorts cuando esté disponible
      if (available_sorts?.values && setAvailableSorts) {
        setAvailableSorts(available_sorts.values);
      }
      if (applied_sort?.value && setAppliedSort) {
        setAppliedSort(applied_sort.value);
      } else if (setAppliedSort) {
        setAppliedSort(undefined);
      }
      if (available_filters && setAllowedStatusIds) {
        const statusFilter = available_filters.find(
          (f) => f.id === "status" && f.type === "multiple_value_filter"
        ) as unknown as { values?: string[] } | undefined;
        setAllowedStatusIds(statusFilter?.values);
      }
      if (setAvailableFilters) setAvailableFilters(available_filters);
    } else {
      const { count, orders, fallenServices } = response;
      if (orders.length) {
        setOrders(getMappedInfo(orders));
        setPagination({ count, page: newPage });
      } else {
        setAlert({
          typeOfAlert: INFO,
          message: "No hay órdenes para mostrar.",
          showAlert: true,
        });
        setOrders([]);
      }
      if (fallenServices?.length)
        setAlert({
          typeOfAlert: INFO,
          message: getFallenServicesMessage(fallenServices),
          showAlert: true,
        });
    }
  } catch (error: unknown) {
    setAlert({
      typeOfAlert: ERROR,
      message: String(error),
      showAlert: true,
    });
  } finally {
    setLoading(false);
  }
};

// eslint-disable-next-line max-lines-per-function
const Orders = ({
  showTitle = true,
  showFilters = true,
  tabsToShow = [],
}: IOrdersComponent) => {
  const {
    currentUser: { entity },
    selectedMarketplace,
    merchantIntegrations,
    rules,
  } = useContext(UserContext);
  const [orders, setOrders] = useState([] as IMappedOrder[]);
  const [selectedOrders, setSelectedOrders] = useState<IOrder[]>([]);
  const [pagination, setPagination] = useState({} as IPaginationParams);
  const [alert, setAlert] = useState({} as IAlert);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [filters, setFilters] = useState({} as IOrdersFilters);
  const [loading, setLoading] = useState(true);
  const [loaderPage, setLoaderPage] = useState(false);
  const [showActions, setShowActions] = useState(true);
  const [aliases, setAliases] = useState<Record<string, string>>({});
  // v2 metadata-driven UI state
  const [availableSorts, setAvailableSorts] = useState<string[]>([]);
  const [appliedSort, setAppliedSort] = useState<string | undefined>(
    undefined
  );
  const [allowedStatusIds, setAllowedStatusIds] = useState<string[] | undefined>(
    undefined
  );
  const [availableFilters, setAvailableFilters] = useState<IOrdersListV2Response["available_filters"]>(
    undefined
  );
  const navigate = useNavigate();

  const fetchAliases = async (marketplaceId?: string) => {
    const aliasDict = await getStatusAliasList(marketplaceId);
    setAliases(aliasDict);
  };

  const getOrderCall = async (newPage?: number) => {
    setLoaderPage(true);
    setAlert({} as IAlert);
    setSelectedOrders([]);
    await getOrders({
      entity,
      setOrders,
      setAlert,
      setPagination,
      setLoading,
      selectedMarketplace,
      filters,
      newPage,
      setAvailableSorts,
      setAppliedSort,
      setAllowedStatusIds,
      setAvailableFilters,
    });
    setLoaderPage(false);
  };

  const handlePageChange = (newPage: number) =>
    getOrderCall(newPage).finally(() => setLoaderPage(false));

  const handleSelectedAction = (action: string, order: IOrder) => {
    switch (action) {
    case "info":
      navigate(`datos-de-orden/${order?.tracking_id}`);
      break;
    case "delete":
      setSelectedOrders([order]);
      setOpenCancelModal(true);
      break;
    case "label":
      downloadFromURL(order.labels);
      break;
    case "refund":
      navigate(`devolucion/${order?.tracking_id}`);
      break;
    case "history":
      navigate(`historial/${order?.tracking_id}`);
      break;
    case "edit":
      setSelectedOrders([order]);
      setOpenEditModal(true);
      break;
    case "view-audit":
      navigate(`orden/${order?.tracking_id}`);
      break;
    default:
      console.warn("Accion inválida");
      break;
    }
  };

  const handleChangeFilter = (
    name: string,
    value: string | number | string[] | number[] | null | undefined
  ) => {
    setFilters((prev) => (prev[name] === value ? prev : { ...prev, [name]: value }));
  };

  const commonProps = {
    showFilters,
    handleChangeFilter,
    alert,
    setAlert,
    aliases,
    loaderPage,
    orders,
    entity,
    pagination,
    handlePageChange,
    handleSelectedAction,
    showActions,
    rules,
    // metadata v2
    availableSorts,
    appliedSort,
    allowedStatusIds,
    availableFilters,
  };

  const tabProps: TTabsProps = {
    [CARRIERS_TAB_ID]: { ...commonProps },
    [OWN_FLEET_TAB_ID]: {
      ...commonProps,
      selectedOrders,
      setSelectedOrders,
      setOpenEditModal,
    },
  };

  useEffect(() => {
    getOrderCall();
  }, [filters]);
  useEffect(() => {
    if (entity === MERCHANT && merchantIntegrations.length > 0) {
      if (Object.keys(filters).length) setFilters({} as IOrdersFilters);
      setShowActions(
        !integrationIsPaused(merchantIntegrations, selectedMarketplace)
      );
    }
    fetchAliases(selectedMarketplace);
  }, [merchantIntegrations, selectedMarketplace]);

  return (
    <>
      {loading ? (
        <Loader size={MEDIUM} />
      ) : (
        <OrdersTabs
          {...{
            showTitle,
            tabProps,
            handleChangeFilter,
            tabsToShow,
            loaderPage,
          }}
        />
      )}

      <CancelOrderModal
        open={openCancelModal}
        setOpen={setOpenCancelModal}
        order={selectedOrders[0] || ({} as IOrder)}
        setFilters={setFilters}
        selectedMarketplace={selectedMarketplace}
      />
      {selectedOrders.length > 0 && (
        <EditOrderModal
          open={openEditModal}
          setOpen={setOpenEditModal}
          selectedOrders={selectedOrders}
          setSelectedOrders={setSelectedOrders}
          setFilters={setFilters}
          selectedMarketplace={selectedMarketplace}
          aliases={aliases}
        />
      )}
    </>
  );
};

export default Orders;
